/**
 * SessionResumeBanner — shown on Home when there is a resumable session.
 * Tapping it navigates to /practice with resume state.
 * Phase 9 will populate this from idb-keyval offline storage.
 */
import { useNavigate }       from 'react-router-dom'
import { motion }            from 'framer-motion'
import { Zap, ArrowRight }   from 'lucide-react'
import type { ActiveSession } from '@/lib/session-storage'
import { ROUTES }            from '@/lib/routes'
import { slideUp }           from '@/lib/motion-variants'
import { useReducedMotion }  from '@/hooks/useReducedMotion'

interface Props {
  session: ActiveSession
}

export function SessionResumeBanner({ session }: Props) {
  const navigate     = useNavigate()
  const reducedMotion = useReducedMotion()

  const handleResume = () =>
    navigate(ROUTES.PRACTICE, {
      state: { resume: true, subject: session.subject, topic: session.topic },
    })

  return (
    <motion.div
      variants={reducedMotion ? {} : slideUp}
      initial="hidden"
      animate="visible"
      className="mx-4 mt-4 bg-primary-light border border-primary/20 rounded-[16px]
                 p-4 flex items-center gap-3 cursor-pointer
                 hover:bg-primary-light/80 transition-colors"
      onClick={handleResume}
      role="button"
      tabIndex={0}
      aria-label={`Resume ${session.subject} — ${session.topic}`}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleResume() }}
    >
      <Zap
        size={20}
        className="text-primary flex-shrink-0"
        fill="currentColor"
        aria-hidden="true"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-primary truncate">
          Resume: {session.topic}
        </p>
        <p className="text-xs text-text-muted">
          {session.subject} · Tap to continue
        </p>
      </div>
      <ArrowRight size={16} className="text-primary flex-shrink-0" aria-hidden="true" />
    </motion.div>
  )
}
